var _a, _b;
import * as tslib_1 from "tslib";
// Third party packages
import { asapScheduler } from 'rxjs';
import { filter, observeOn, pairwise, startWith } from 'rxjs/operators';
/**
 * "Servizi aggiuntivi" of a company's contractual conditions.
 *
 * Each service is enabled by its own `subscription.<flag>` and priced by
 * `subscription.<flag>Fee`; they are independent of `documentUpload` and
 * drive which sections the Master sees in "my company".
 *
 * @since 3.0.0
 */
export var ADDITIONAL_SERVICE_FLAGS = [
    'safetyVideoResidents',
    'safetyVideoSuppliers',
    'fireSafety',
    'informativeNote',
];
/**
 * Services that used to be enabled all together by the single legacy
 * `subscription.additionalServices` flag (priced by `additionalServicesFee`).
 * While a company has never saved its own value for one of them, that service
 * inherits the legacy flag (lazy migration: the first contract save makes it explicit).
 */
export var LEGACY_ADDITIONAL_SERVICE_FLAGS = [
    'safetyVideoResidents',
    'safetyVideoSuppliers',
    'fireSafety',
];
/**
 * Services pre-selected when a new company is created with the document upload
 * active (the default). The "Nota informativa" is never pre-selected.
 */
export var DEFAULT_ADDITIONAL_SERVICE_FLAGS = [
    'safetyVideoResidents',
    'safetyVideoSuppliers',
    'fireSafety',
];
/**
 * Services each service requires: they are turned on together with it and cannot
 * be turned off while it is on. The "Nota informativa" holds the QR codes of every
 * additional service and the condo-owner privacy access key.
 * Mirrored on the back-end by utils/subscription-service-links.ts.
 */
export var SERVICE_REQUIREMENTS = {
    informativeNote: ['safetyVideoResidents', 'safetyVideoSuppliers', 'fireSafety', 'privacy'],
    suppliers: ['safetyVideoSuppliers'],
};
/** Services that cannot be turned on while the given flags are on */
export var SERVICE_EXCLUSIONS = {
    informativeNote: ['privacySag'],
};
/**
 * Whether an additional service is active for the given contractual conditions:
 * an explicitly saved flag wins; a never-saved legacy service inherits
 * `additionalServices`; anything else is off.
 *
 * @since 3.0.0
 *
 * @param subscription Company contractual conditions
 * @param flag Service flag key
 * @returns true when the service is active
 */
export function isAdditionalServiceActive(subscription, flag) {
    if (!subscription) {
        return false;
    }
    var value = subscription[flag];
    if (value === true || value === false) {
        return value;
    }
    return LEGACY_ADDITIONAL_SERVICE_FLAGS.indexOf(flag) > -1 && subscription.additionalServices === true;
}
/**
 * Fee to show for an additional service: the saved one when present, otherwise
 * (company saved before the split) the legacy single `additionalServicesFee`
 * for the services it used to cover, otherwise 0.
 *
 * @since 3.0.0
 *
 * @param subscription Company contractual conditions
 * @param flag Service flag key (its fee is stored in `${flag}Fee`)
 * @returns Fee value
 */
export function additionalServiceFee(subscription, flag) {
    if (!subscription) {
        return 0;
    }
    var saved = subscription[flag + "Fee"];
    if (typeof saved === 'number' && isFinite(saved)) {
        return saved;
    }
    var legacy = subscription.additionalServicesFee;
    if (LEGACY_ADDITIONAL_SERVICE_FLAGS.indexOf(flag) > -1 && typeof legacy === 'number' && isFinite(legacy)) {
        return legacy;
    }
    return 0;
}
var ɵ2 = function (service) { return SERVICE_REQUIREMENTS[service]; }, ɵ1 = function (service) { return SERVICE_EXCLUSIONS[service]; }, ɵ0 = function (flag, index, flags) { return flags.indexOf(flag) === index; };
/** Every subscription flag involved in a service link */
var LINKED_SERVICE_FLAGS = (_a = (_b = Object.keys(SERVICE_REQUIREMENTS)).concat.apply(_b, tslib_1.__spread(Object.keys(SERVICE_REQUIREMENTS).map(ɵ2)))).concat.apply(_a, tslib_1.__spread(Object.keys(SERVICE_EXCLUSIONS).map(ɵ1))).filter(ɵ0);
/**
 * Active services that require the given flag (e.g. "privacy" is required by an
 * active "Nota informativa"): while this list is not empty the flag cannot be turned off.
 *
 * @since 3.0.0
 *
 * @param subscription Current contractual conditions (form value)
 * @param flag Subscription flag key
 * @returns Keys of the active services requiring the flag
 */
export function activeServicesRequiring(subscription, flag) {
    return Object.keys(SERVICE_REQUIREMENTS).filter(function (service) { return SERVICE_REQUIREMENTS[service].indexOf(flag) > -1 && isAdditionalServiceActive(subscription, service); });
}
/**
 * Active flags that forbid turning the given service on (e.g. "privacySag" for the
 * "Nota informativa", which requires the standard privacy).
 *
 * @since 3.0.0
 *
 * @param subscription Current contractual conditions (form value)
 * @param service Subscription flag key of the service being turned on
 * @returns Keys of the active flags excluding the service
 */
export function activeExclusions(subscription, service) {
    var exclusions = SERVICE_EXCLUSIONS[service] || [];
    return exclusions.filter(function (flag) { return isAdditionalServiceActive(subscription, flag); });
}
/**
 * Snapshot of the linked flags of a subscription form group. Read control by control
 * so disabled controls (e.g. "privacySag" while "privacy" is on) are included.
 */
function readLinkedFlags(group) {
    var e_1, _a;
    var values = {};
    try {
        for (var LINKED_SERVICE_FLAGS_1 = tslib_1.__values(LINKED_SERVICE_FLAGS), LINKED_SERVICE_FLAGS_1_1 = LINKED_SERVICE_FLAGS_1.next(); !LINKED_SERVICE_FLAGS_1_1.done; LINKED_SERVICE_FLAGS_1_1 = LINKED_SERVICE_FLAGS_1.next()) {
            var flag = LINKED_SERVICE_FLAGS_1_1.value;
            var control = group.get(flag);
            if (control) {
                values[flag] = control.value;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (LINKED_SERVICE_FLAGS_1_1 && !LINKED_SERVICE_FLAGS_1_1.done && (_a = LINKED_SERVICE_FLAGS_1.return)) _a.call(LINKED_SERVICE_FLAGS_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return values;
}
/**
 * Enforce the links between services on the contract form, for user changes only
 * (programmatic changes, e.g. loading the company, leave the control pristine):
 *  - turning a service on turns on the services it requires, unless an excluding flag
 *    is active: then the change is reverted and refused;
 *  - turning a service off while an active service requires it is reverted and refused.
 * Each check runs once the current change has reached every other listener (asap
 * scheduler), so reverting cannot interleave with their emissions.
 *
 * @since 3.0.0
 *
 * @param group Subscription form group ("subscription" of the contract form)
 * @param onRefused Called after a refused change has been reverted
 * @param onError Called on an unexpected stream error
 * @returns Subscriptions to release on destroy
 */
export function bindServiceLinks(group, onRefused, onError) {
    var e_2, _a;
    var subscriptions = [];
    var _loop_1 = function (flag) {
        var control = group.get(flag);
        if (!control) {
            return "continue";
        }
        subscriptions.push(control.valueChanges
            .pipe(startWith(control.value), pairwise(), filter(function (_a) {
            var _b = tslib_1.__read(_a, 2), previous = _b[0], current = _b[1];
            return (previous === true) !== (current === true) && control.dirty;
        }), observeOn(asapScheduler))
            .subscribe(function (_a) {
            var e_3, _b;
            var _c = tslib_1.__read(_a, 2), current = _c[1];
            var turnedOn = current === true;
            // Superseded by a later change: that change is checked on its own
            if ((control.value === true) !== turnedOn) {
                return;
            }
            var values = readLinkedFlags(group);
            if (turnedOn) {
                if (activeExclusions(values, flag).length) {
                    control.setValue(false);
                    onRefused('excludedByActiveService');
                    return;
                }
                try {
                    for (var _d = (e_3 = void 0, tslib_1.__values(SERVICE_REQUIREMENTS[flag] || [])), _e = _d.next(); !_e.done; _e = _d.next()) {
                        var required = _e.value;
                        var requiredControl = group.get(required);
                        if (requiredControl && requiredControl.value !== true) {
                            requiredControl.setValue(true);
                        }
                    }
                }
                catch (e_3_1) { e_3 = { error: e_3_1 }; }
                finally {
                    try {
                        if (_e && !_e.done && (_b = _d.return)) _b.call(_d);
                    }
                    finally { if (e_3) throw e_3.error; }
                }
                return;
            }
            if (activeServicesRequiring(values, flag).length) {
                control.setValue(true);
                onRefused('requiredByActiveService');
            }
        }, onError));
    };
    try {
        for (var LINKED_SERVICE_FLAGS_2 = tslib_1.__values(LINKED_SERVICE_FLAGS), LINKED_SERVICE_FLAGS_2_1 = LINKED_SERVICE_FLAGS_2.next(); !LINKED_SERVICE_FLAGS_2_1.done; LINKED_SERVICE_FLAGS_2_1 = LINKED_SERVICE_FLAGS_2.next()) {
            var flag = LINKED_SERVICE_FLAGS_2_1.value;
            _loop_1(flag);
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (LINKED_SERVICE_FLAGS_2_1 && !LINKED_SERVICE_FLAGS_2_1.done && (_a = LINKED_SERVICE_FLAGS_2.return)) _a.call(LINKED_SERVICE_FLAGS_2);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return subscriptions;
}
export { ɵ2, ɵ1, ɵ0 };
